import { useState, useEffect, useCallback, useRef, useMemo, useContext } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import LocationService from "../service/locationService.mjs";
import CurrencyService from "../service/currencyService.mjs";
import { debounce } from "lodash";
import ExpenseService from "../service/expenseReimbursementService.mjs"
import InvoiceService from "../service/invoiceUploadService.mjs";
import { secureLogger } from '../../utils/secureLogger.mjs';
import { AuthContext } from '../../authProvider.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import { parseLocalDate, formatLocalDate } from '../../utils/dateOnly.mjs';
import { linesNeedingPurpose, draftPurposeReminder, DESCRIPTION_TEMPLATE } from '../../utils/expensePurpose.mjs';

// InvoiceService exports a class, and its methods are instance methods -
// calling them on the class throws "is not a function". Instantiate once.
const invoiceService = new InvoiceService();

// The statuses in which the server takes an admin's edit (claimAccess.mjs,
// ADMIN_EDITABLE_STATUSES): with the admin for review, sent back to the
// claimant, or returned by the auditor. Approved, paid and rejected are not.
const ADMIN_EDITABLE_STATUSES = ['submitted', 'returned', 'under_review'];

// -- Exchange rates ----------------------------------------------------------
// A rate is foreign units per USD. USD is 1 by definition; every other currency
// needs a rate that was actually looked up. A missing rate used to fall back to
// 1, so 6,520 ETB whose lookup had failed (or not come back yet) was claimed as
// $6,520 - and invoice reading now fills the currency on many rows at once.
const isUsdCurrency = (code) => String(code || '').trim().toUpperCase() === 'USD';

// The row's own rate when it has one, else 1 for USD and 0 for anything else.
// 0 is deliberate: the row shows no USD figure, the auto-fetch prices it, and
// submitting names the line until a real rate arrives.
const rateOrFallback = (currency, rate) => {
  if (parseFloat(rate) > 0) return rate;
  return isUsdCurrency(currency) ? 1 : 0;
};

// USD for an amount at a rate, 2dp; nothing when the row has no usable rate.
const usdAt = (amount, rate) => {
  const r = parseFloat(rate);
  return r > 0 ? Math.round(((parseFloat(amount) || 0) / r) * 100) / 100 : 0;
};

// Every non-USD line with a currency but no rate above 0, as
// `[{ line, currency, amount }]` with 1-based line numbers. Shared by the
// claimant's submit block below and the admin's approve warning.
export const findUnpricedLines = (items) => (items || []).reduce((acc, item, index) => {
  const currency = String(item?.expenditureCurrency || '').trim().toUpperCase();
  if (currency && !isUsdCurrency(currency) && !(parseFloat(item?.exchangeRate) > 0)) {
    acc.push({ line: index + 1, currency, amount: parseFloat(item?.receiptAmount) || 0 });
  }
  return acc;
}, []);

// Submit-time check: every non-USD line with a currency must carry a rate
// above 0. Drafts skip it, so a draft can be saved while a rate is pending.
const unpricedLinesMessage = (items) => {
  const unpriced = findUnpricedLines(items);
  if (unpriced.length === 0) return null;
  if (unpriced.length === 1) {
    const [{ line, currency }] = unpriced;
    return `Line ${line}: the exchange rate for ${currency} has not loaded yet - wait a moment or re-select the currency.`;
  }
  const shown = unpriced.slice(0, 5).map(({ line, currency }) => `${line} (${currency})`).join(', ');
  const more = unpriced.length > 5 ? ` and ${unpriced.length - 5} more` : '';
  return `Lines ${shown}${more}: the exchange rate has not loaded yet - wait a moment or re-select the currency.`;
};

// Admin approve/resubmit: the same lines WARN instead of blocking. The server
// prices a line with no rate 1:1, and an admin who can see the claim may have
// no published rate to wait for - blocking left such a claim stuck. So the
// admin is told exactly what 1:1 means in dollars and chooses. `verb` is the
// button they pressed ('Approve', 'Save'). Null when every line is priced.
const formatAmount = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });
export const unpricedLinesWarning = (items, verb = 'Approve') => {
  const unpriced = findUnpricedLines(items);
  if (unpriced.length === 0) return null;
  const describe = ({ line, currency, amount }) =>
    `Line ${line} (${currency} ${formatAmount(amount)})`;
  if (unpriced.length === 1) {
    const [only] = unpriced;
    return `${describe(only)} has no exchange rate and will be paid at a rate of 1 ` +
      `(USD ${formatAmount(only.amount)}). ${verb} anyway?`;
  }
  const shown = unpriced.slice(0, 5)
    .map((u) => `- ${describe(u)} will be paid as USD ${formatAmount(u.amount)}`)
    .join('\n');
  const more = unpriced.length > 5 ? `\n- and ${unpriced.length - 5} more` : '';
  return `${unpriced.length} lines have no exchange rate and will be paid at a rate of 1:\n` +
    `${shown}${more}\n\n${verb} anyway?`;
};



const useExpenseForm = (adminMode = false) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();
  const { isAuditor, user } = useContext(AuthContext);
  const location = useLocation();
  const { claimId } = useParams();
  const navigate = useNavigate();

  // Add state to track current working claim ID
  const [currentClaimId, setCurrentClaimId] = useState(claimId);


  // Form state
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [position, setPosition] = useState('');



  // Get the data passed via navigation state
  const initialExtractedData = location.state?.extractedData || [];
  const navigationClaimId = location.state?.claimId;


   // Check if this is a create route
  const isCreateRoute = useMemo(() => {
    return window.location.pathname.includes('/create');
  }, []);
 
  

  
  // Determine admin mode
  const isAdminMode = adminMode || window.location.pathname.includes('/admin/');
  
  const isViewMode = !!claimId && !isCreateRoute;
  const [isEditing, setIsEditing] = useState(false);
  
  // Status management state
  const [claimStatus, setClaimStatus] = useState('draft');
  const [claimData, setClaimData] = useState(null);
  const [adminComments, setAdminComments] = useState('');
  const [processedBy, setProcessedBy] = useState(null);
  const [processedAt, setProcessedAt] = useState(null);
  
  // Admin modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [adminDecisionComment, setAdminDecisionComment] = useState('');
  


  const canEditClaim = useMemo(() => {
    // Create route / brand-new claims are editable.
    if (isCreateRoute) return true;
    if (!claimId) return true;
    // Users can edit their own claim while it is a draft or was returned for
    // revision; submitted/approved claims are view-only (admins edit via admin form).
    return claimStatus === 'draft' || claimStatus === 'returned';
  }, [claimId, isCreateRoute, claimStatus]);

  // Determine read-only state.
  // Editable claims (create route, the user's own draft, or a returned claim)
  // are directly editable - we no longer require a separate "Edit" click, since
  // the draft banner/Edit button was removed. Everything else is view-only
  // (admins edit through the admin form, which forces isAdminMode).
  const isReadOnly = useMemo(() => {
    // Auditors review OTHER PEOPLE's claims read-only. They open those through
    // the /admin/ routes, which would otherwise grant edit rights, so this keys
    // off the ROLE rather than the URL - but it must not extend to their own
    // claims. An auditor is also an employee who files per diem and expenses,
    // and an unconditional lock here left them unable to fill in a new claim at
    // all (the backend never restricted this; the form was simply read-only).
    const reviewingSomeoneElse =
      claimData?.user && user?.id && String(claimData.user.id) !== String(user.id);
    if (isAuditor && (isAdminMode || reviewingSomeoneElse)) return true;
    // A claim sitting in a report under auditor/director review is frozen even
    // for admins; it unlocks only when the report is sent back for revision.
    if (claimData?.reportLocked) return true;
    // An admin edits a claim while it is with them - submitted, sent back to
    // the claimant, or returned by the auditor - which is when the server
    // accepts their save (ADMIN_EDITABLE_STATUSES). Once they approve it, it
    // is with the auditor, and it is read-only here too. This used to be
    // `return false`: approved, paid and rejected claims looked editable to
    // the admin - currency, description, the no-receipt box, the city search -
    // and the rates kept auto-fetching into them, none of it saveable.
    if (isAdminMode) return !ADMIN_EDITABLE_STATUSES.includes(claimStatus);
    return !canEditClaim;
  }, [isAdminMode, canEditClaim, claimData, claimStatus, isAuditor, user]);

  
  const [expenseItems, setExpenseItems] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [cityResults, setCityResults] = useState([]);
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Line ids a refused submit found without a what/why. A new array on every
  // refusal, so the form scrolls to the first one again even if it is the same.
  const [purposeAlertIds, setPurposeAlertIds] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const _initialized = useRef(false);

  // hook to fetch exchange rates
  const [autoFetchExchangeRates, setAutoFetchExchangeRates] = useState(true);
  const [_pendingExchangeRateItems, setPendingExchangeRateItems] = useState(new Set());

  // Add effect to handle URL-based claimId updates
  useEffect(() => {
    // Extract claimId from current URL if it's not in params
    const urlPath = window.location.pathname;
    const urlClaimIdMatch = urlPath.match(/\/claim\/expense\/([a-fA-F0-9-]+)/);

    if (urlClaimIdMatch && urlClaimIdMatch[1] !== claimId) {
      setCurrentClaimId(urlClaimIdMatch[1]);
    } else if (claimId) {
      setCurrentClaimId(claimId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]); // window.location.pathname intentionally omitted (not a valid React dep)

  
  // Enable editing function
  const enableEditing = useCallback(() => {
    if (!canEditClaim) {
      setError(`Cannot edit claim with status: ${claimStatus}. Only draft and returned claims can be edited.`);
      return;
    }
    setIsEditing(true);
  }, [canEditClaim, claimStatus]);

  // Load claim data function
  const loadClaimData = useCallback(async (id) => {
  try {
    setIsLoading(true);
    
    if (!id || typeof id !== 'string' || id.trim() === '') {
      throw new Error('Invalid claim ID format');
    }
    
    const claimData = await ExpenseService.getExpenseClaim(id);
    
    if (claimData) {
      // Set claim status and metadata
      setClaimStatus(claimData.status || 'draft');
      setClaimData(claimData);
      setAdminComments(claimData.adminComments || '');
      setProcessedBy(claimData.processedBy || null);
      setProcessedAt(claimData.processedAt || null);
      
      if (claimData.items && claimData.items.length > 0) {
        const uniqueItemsMap = new Map();
        for (const item of claimData.items) {
          // Keyed on the ROW, not the invoice. invoiceId is null for every
          // manually entered line, so keying on it collapsed all of them into a
          // single row - real data loss, and it also destroyed the row identity
          // the server now needs to keep a line's PTAE across an edit.
          // crypto.randomUUID() stays only as a last resort for a row that has
          // never been saved and so genuinely has no id yet.
          const rowKey = item._id || item.id || `new:${item.invoiceId || ''}:${uniqueItemsMap.size}`;
          if (!uniqueItemsMap.has(rowKey)) {
            uniqueItemsMap.set(rowKey, {
              id: item._id || item.id || crypto.randomUUID(),
              invoiceId: item.invoiceId,
              // Local midnight of the stored calendar day. new Date('YYYY-MM-DD')
              // is UTC midnight, which is the previous day west of UTC, and the
              // save and the rate lookup both read the LOCAL day back.
              expenditureDate: parseLocalDate(item.expenditureDate),
              receiptNumber: item.receiptNumber || '',
              expenditureLocation: item.expenditureLocation || '',
              expenditureCurrency: item.expenditureCurrency || '',
              expenditureDescription: item.expenditureDescription || '',
              receiptAmount: item.receiptAmount || 0,
              // Not `|| 1`: a draft saved while a rate was missing reloaded as
              // 1:1 and then passed the submit check. Left at 0 it is priced
              // by the auto-fetch instead (see rateOrFallback).
              exchangeRate: rateOrFallback(item.expenditureCurrency, item.exchangeRate),
              usdEquivalent: item.usdEquivalent || 0,
              comments: item.comments || '',
              adminComments: item.adminComments || '',
              invoicePdfGoogleCloudUrl: item.invoicePdfGoogleCloudUrl || null,
              pdfUrl: item.pdfUrl || item.invoicePdfGoogleCloudUrl || null,
              // Carried through so the form can show a file the virus scan
              // removed; missing on older responses, read as 'unscanned'.
              scanStatus: item.scanStatus,
              status: item.status || 'pending'
            });
          }
        }
        const formattedItems = Array.from(uniqueItemsMap.values());
        setExpenseItems(formattedItems);
        // Remove this line: setItemsLoaded(true); - it's handled in the useEffect
      }
    }

  } catch (err) {
    secureLogger.error('Error loading claim data', { status: err.response?.status });
    setError('Failed to load existing expense data: ' + (err.message || 'Unknown error'));
    throw err; // Re-throw so the useEffect can catch it and reset itemsLoaded
  } finally {
    setIsLoading(false);
  }
}, []);

  useEffect(() => {
    if (isCreateRoute) return;
    
    // Priority 1: Use navigation state data if available
    if (initialExtractedData.length > 0 && !itemsLoaded) {
      
      const formattedItems = initialExtractedData.map(data => ({
        id: crypto.randomUUID(),
        invoiceId: data.invoiceId,
        expenditureDate: parseLocalDate(data.expenditureDate),
        receiptNumber: data.receiptNumber || '',
        location: data.expenditureLocation || '',
        expenditureCurrency: data.expenditureCurrency || '',
        expenditureDescription: data.expenditureDescription || '',
        receiptAmount: data.receiptAmount || 0,
        exchangeRate: rateOrFallback(data.expenditureCurrency, data.exchangeRate),
        usdEquivalent: data.usdEquivalent || 0,
        comments: '',
        adminComments: data.adminComments || '',
        invoicePdfGoogleCloudUrl: data.pdfUrl || null,
        pdfUrl: data.pdfUrl || null,
        scanStatus: data.scanStatus,
        status: data.status || 'pending'
      }));
      
      setExpenseItems(formattedItems);
      setItemsLoaded(true);
      
      // Also set claim metadata if available
      if (navigationClaimId) {
        setClaimStatus('draft'); // Newly uploaded claims are drafts
        setClaimData({ claimId: navigationClaimId });
      }
      
      return; // Exit early - don't load from API
    }
    
    // Priority 2: Load from API if no navigation state and claimId exists
    if (claimId && !itemsLoaded && initialExtractedData.length === 0) {
    
      setItemsLoaded(true); // Prevent re-runs
      
      // Handle the Promise properly - loadClaimData doesn't return data, it updates state
      loadClaimData(claimId)
        .then(() => {
          // loadClaimData updates state internally, no data is returned
         
        })
        .catch(error => {
          secureLogger.error('Error loading claim data', { status: error.response?.status });
          // Reset itemsLoaded on error so user can retry
          setItemsLoaded(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, isCreateRoute, itemsLoaded, loadClaimData, navigationClaimId]); // initialExtractedData intentionally omitted (logical expression changes every render)


  // Admin action handlers
  const withdrawApproval = useCallback(async () => {
    const id = claimId || currentClaimId;
    if (!id) return;
    if (!await confirm(
      'Withdraw the approval on this claim?\n\nIt will return to Under Review, ' +
      'leave the auditor queue, and become editable again.'
    )) return;
    setIsLoading(true);
    setError(null);
    try {
      await ExpenseService.withdrawApproval(id);
      await loadClaimData(id);
    } catch (err) {
      const serverMessage = err.response?.data?.message || err.message;
      setError(`Could not withdraw this approval: ${serverMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [claimId, currentClaimId, loadClaimData, confirm]);
  const handleAdminAction = useCallback(() => {
    setShowActionModal(true);
  }, []);

  // Unpriced non-USD lines no longer block an admin: the server pays them 1:1
  // and says so in a warning, so the admin is asked instead (see
  // unpricedLinesWarning). A save that RESUBMITS - the claim is 'returned' and
  // every admin save sends status 'submitted' - is asked here, and so is a
  // save of a claim 'under_review': a flagged or auditor-returned claim can go
  // back to approved without another admin approve, so this save is the last
  // point anyone confirms its 1:1 lines.
  // `warnUnpriced` is off for the save inside handleModalSubmit, which has
  // already asked for an approve and needs no question for a reject/send-back.
  // Resolves false when the admin cancels, so a caller can stop.
  const saveAdminChanges = useCallback(async ({ warnUnpriced = true } = {}) => {
    const urlPath = window.location.pathname;
    const urlClaimIdMatch = urlPath.match(/\/claim\/expense\/([a-fA-F0-9-]+)/);
    const workingClaimId = urlClaimIdMatch ? urlClaimIdMatch[1] : (claimId || claimData?.claimId || currentClaimId);

    if (!workingClaimId) {
      setError('Cannot save: missing claim ID');
      return;
    }

    if (warnUnpriced && (claimStatus === 'returned' || claimStatus === 'under_review')) {
      const verb = claimStatus === 'returned' ? 'Save and resubmit' : 'Save';
      const warning = unpricedLinesWarning(expenseItems, verb);
      if (warning && !await confirm(warning, {
        title: 'Lines without an exchange rate',
        confirmLabel: verb,
      })) return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const sent = expenseItems;
      const adminClaimData = {
        items: sent,
        totalAmount: sent.reduce((sum, item) => sum + (parseFloat(item.usdEquivalent) || 0), 0),
        isAdminEdit: true
      };

      const result = await ExpenseService.adminUpdateExpenseClaim(workingClaimId, adminClaimData, false);

      // A line added here has an id made up in the browser, and the server
      // stores it under one of its own. Without taking that id the form kept
      // the made-up one: the PTAE panel showed the saved line as removed, and
      // the next save - the one Approve makes first - matched nothing,
      // recreated the line, and dropped the PTAE just assigned to it.
      const savedIds = Array.isArray(result?.lineIds) ? result.lineIds : [];
      const renamed = new Map();
      sent.forEach((item, i) => {
        const stored = savedIds[i];
        if (stored && String(stored) !== String(item.id)) renamed.set(String(item.id), String(stored));
      });
      if (renamed.size > 0) {
        setExpenseItems(prev => prev.map(item => (
          renamed.has(String(item.id)) ? { ...item, id: renamed.get(String(item.id)) } : item
        )));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      return true;
    } catch (err) {
      secureLogger.error('Error saving admin changes', { status: err.response?.status });
      setError('Failed to save changes: ' + (err.response?.data?.message || err.message));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [expenseItems, claimId, claimData, currentClaimId, claimStatus, confirm]);

  const handleModalSubmit = useCallback(async () => {
    if (!selectedAction) {
      setError('Please select an action');
      return;
    }
    
    if ((selectedAction === 'reject' || selectedAction === 'sendBack') && !adminDecisionComment.trim()) {
      setError('Comments are required for rejection or send back actions');
      return;
    }

    // Asked before anything is saved, so a Cancel leaves the claim untouched
    // and the action modal open.
    if (selectedAction === 'approve') {
      const warning = unpricedLinesWarning(expenseItems, 'Approve');
      if (warning && !await confirm(warning, {
        title: 'Lines without an exchange rate',
        confirmLabel: 'Approve',
      })) return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const statusMap = {
        'approve': 'approved',
        'reject': 'rejected', 
        'sendBack': 'returned'
      };

      const actionData = {
        action: statusMap[selectedAction],
        comments: adminDecisionComment,
        lineItemComments: expenseItems
          .map((item, index) => ({
            itemId: item.id,
            description: item.expenditureDescription,
            adminComments: item.adminComments || '',
            index
          }))
          .filter(comment => comment.adminComments.trim() !== '')
      };

      // Save edited field values before submitting the action
      await saveAdminChanges({ warnUnpriced: false });

      await ExpenseService.submitAdminAction(claimId, actionData);

      setShowActionModal(false);
      setSelectedAction('');
      setAdminDecisionComment('');
      
      const actionText = selectedAction === 'approve' ? 'approved' : 
                        selectedAction === 'reject' ? 'rejected' : 'sent back for revision';
      alert(`Claim has been ${actionText} successfully!`);
      
      window.location.href = '/admin/dashboard';
      
    } catch (error) {
      secureLogger.error('Failed to process admin action', { status: error.response?.status });
      // Surface the server's reason - it names what is actually missing (an
      // unassigned PTAE, a claim locked into a report under review). The old
      // blanket "please try again" hid that, leaving the admin to retry an
      // action that could never succeed.
      const serverMessage = error.response?.data?.message || error.message;
      setError(
        serverMessage
          ? `Could not ${selectedAction} this claim: ${serverMessage}`
          : `Failed to ${selectedAction} claim. Please try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedAction, adminDecisionComment, expenseItems, claimId, setError, setIsLoading, saveAdminChanges, alert, confirm]);

  // Load claim data on mount if editing existing claim
  useEffect(() => {
    
    if (claimId && !itemsLoaded) {
      loadClaimData(claimId);
    }
  }, [claimId, isCreateRoute, itemsLoaded, loadClaimData]);

  // A browser Back into this form can be served from the back/forward cache,
  // which restores the React state captured BEFORE an approval - so a claim that
  // is now approved still renders as editable. Only `persisted` restores are
  // re-read, so ordinary tab switching never discards unsaved edits.
  useEffect(() => {
    const targetId = claimId || currentClaimId;
    if (!targetId) return;

    const handlePageShow = (event) => {
      if (event.persisted) loadClaimData(targetId);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [claimId, currentClaimId, loadClaimData]);

  // Updated save function with status logic
  const saveExpenses = useCallback(async (isDraft = false, isAdminEdit = false) => {
    if (isReadOnly && !isAdminEdit) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      const itemsToSave = expenseItems;
      
      // Skip validation for drafts
      if (!isDraft) {
        if (itemsToSave.length === 0) {
          setError('Please add at least one expense item');
          return;
        }

        // Report exactly which line is missing what, rather than a blanket
        // "fill in all required fields" the user then has to hunt through.
        const REQUIRED_FIELDS = [
          { key: 'expenditureDate',        label: 'Date' },
          { key: 'receiptNumber',          label: 'Receipt / Invoice No.' },
          { key: 'expenditureLocation',    label: 'Location' },
          { key: 'expenditureDescription', label: 'Description' },
          { key: 'receiptAmount',          label: 'Amount' },
          { key: 'expenditureCurrency',    label: 'Currency' },
        ];

        const problems = itemsToSave.reduce((acc, item, index) => {
          const missing = REQUIRED_FIELDS
            .filter(f => {
              const v = item[f.key];
              // 0 is not a valid amount, but guard against '' and null generally.
              return v === undefined || v === null || String(v).trim() === '' || (f.key === 'receiptAmount' && !(parseFloat(v) > 0));
            })
            .map(f => f.label);
          if (missing.length > 0) acc.push(`Line ${index + 1}: ${missing.join(', ')}`);
          return acc;
        }, []);

        if (problems.length > 0) {
          const detail = problems.slice(0, 5).join(' • ');
          const more = problems.length > 5 ? ` • …and ${problems.length - 5} more line(s)` : '';
          setError(`Missing required information - ${detail}${more}`);
          return;
        }

        // Every line must say what was bought and why after "what/why:" - its
        // business purpose. A description filled in from an invoice is only a
        // stub ("who: X, what/why: , when: 15-03-2025"), and a line typed by
        // hand or with its "what/why:" deleted may have no purpose at all;
        // all are non-empty, so the check above lets them through. Same rule
        // as the server's. The ids go to the form, which scrolls to the first
        // such line, puts the caret in its what/why slot and marks every one
        // of them red.
        const noPurpose = linesNeedingPurpose(itemsToSave);
        if (noPurpose.length > 0) {
          setPurposeAlertIds(noPurpose.map(p => p.id));
          setError(
            `Add what was bought and why to the description on line${noPurpose.length > 1 ? 's' : ''} ` +
            `${noPurpose.map(p => p.line).join(', ')}, after "what/why:" - the business purpose is required to submit.`
          );
          return;
        }
        setPurposeAlertIds([]);

        // A foreign-currency line whose rate never arrived has no honest USD
        // figure. Refused here rather than saved at 1:1; the rate is fetched in
        // the background, so usually the fix is just to wait a moment.
        const unpriced = unpricedLinesMessage(itemsToSave);
        if (unpriced) {
          setError(unpriced);
          return;
        }

        // Every line without an attached receipt must be flagged "No receipt
        // available" and carry a comment explaining why.
        const missingReceiptJustification = itemsToSave.filter(item => {
          const hasFile = item.invoicePdfGoogleCloudUrl || item.pdfUrl;
          if (hasFile) return false;
          return !(item.noReceipt && String(item.comments || '').trim());
        });
        if (missingReceiptJustification.length > 0) {
          setError('For each line without a receipt, tick "No receipt available" and add a comment explaining why.');
          return;
        }
      }
      
      let result;
      
      // Get current claim ID
      const getCurrentClaimId = () => {
        const urlPath = window.location.pathname;
        const urlClaimIdMatch = urlPath.match(/\/claim\/expense\/([a-fA-F0-9-]+)/);
        if (urlClaimIdMatch) return urlClaimIdMatch[1];
        
        return claimId || claimData?.claimId || currentClaimId;
      };

      const workingClaimId = getCurrentClaimId();
      
      if (isAdminEdit) {
        // Admin edit mode
        const claimData = {
          items: itemsToSave,
          totalAmount: itemsToSave.reduce((sum, item) => sum + (parseFloat(item.usdEquivalent) || 0), 0),
          isAdminEdit: true,
          adminComments: itemsToSave.map(item => ({
            itemId: item.id,
            comment: item.adminComments || '',
            description: item.expenditureDescription
          })).filter(comment => comment.comment)
        };
        
        result = await ExpenseService.adminUpdateExpenseClaim(workingClaimId, claimData, isDraft);
        alert('Admin changes saved successfully!');
        window.location.href = `/admin/dashboard`;
        
      } else if (workingClaimId) {
        // Updating existing claim (including manual claims)
        result = await ExpenseService.updateExpenseClaim(workingClaimId, itemsToSave, isDraft);

        // A claim this admin is filing for someone else. Same save, same
        // rules; it just ends back on the admin's own dashboard, and says whose
        // claim it was.
        const filedFor = claimData?.filedOnBehalfBy
          && String(claimData?.userId) !== String(user?.id)
          ? `${claimData?.user?.firstName || ''} ${claimData?.user?.lastName || ''}`.trim()
          : null;

        // A draft may still lack its purposes - saving is allowed - but the
        // confirmation says how many lines submitting will need them on.
        const reminder = isDraft ? draftPurposeReminder(itemsToSave) : '';

        if (filedFor) {
          alert(isDraft
            ? `Draft saved for ${filedFor}. It stays hidden from them until you submit it.${reminder}`
            : `Claim filed for ${filedFor} and submitted. They have been emailed.`);
          navigate(isDraft ? '/admin/file-claim' : '/admin/dashboard');
        } else {
          alert(isDraft ? `Draft updated successfully!${reminder}` : 'Claim submitted successfully!');
          navigate('/dashboard');
        }

      } else {
        // Creating completely new claim (shouldn't happen with manual flow)
        result = await ExpenseService.saveExpenseClaim(null, itemsToSave, isDraft);
        alert(isDraft
          ? `Draft saved successfully!${draftPurposeReminder(itemsToSave)}`
          : 'Claim submitted successfully!');
        navigate('/dashboard');
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
      
      return result;
      
    } catch (err) {
      secureLogger.error('Error saving expenses', { status: err.response?.status });
      const errorMessage = err.response?.data?.message || err.message || 'Please try again.';
      setError('Failed to save expenses: ' + errorMessage);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [expenseItems, isReadOnly, claimId, claimData, currentClaimId, navigate, alert, user]);

  // Add a new expense item - this is the function exposed to the component
  const addExpenseItem = useCallback(() => {
    if (isReadOnly) return; // Don't allow adding items in read-only mode
    
    const newItem = {
      id: crypto.randomUUID(),
      invoiceId: null,
      expenditureDate: new Date(),
      // Today is only a starting value: until the user picks a date, reading an
      // attached receipt may replace it (mergeExtractionIntoRow treats it as empty).
      expenditureDateIsDefault: true,
      expenditureLocation: '',
      receiptNumber: '',
      location: '',
      expenditureCurrency: '',
      // The who / what/why / when template, so the business-purpose slot the
      // submit check asks for is there from the start.
      expenditureDescription: DESCRIPTION_TEMPLATE,
      receiptAmount: 0,
      exchangeRate: 0,
      usdAmount: 0,
      comments: ''
    };
    
    setExpenseItems(prev => [...prev, newItem]);
  }, [isReadOnly]);

  // Delete an expense item
  const deleteExpenseItem = useCallback((id) => {
    if (isReadOnly) return; // Don't allow deleting items in read-only mode
    
    setExpenseItems(prev => prev.filter(item => item.id !== id));
  }, [isReadOnly]);

  // Search for cities
  const searchCities = useCallback(async (query, itemId) => {
    if (query.length < 2 || isReadOnly) {
      setCityResults([]);
      return;
    }
    
    setIsSearching(true);
    setActiveSearchId(itemId);
    
    try {
      const results = await LocationService.searchCities(query);
      setCityResults(results);
    } catch (err) {
      secureLogger.error('Error searching cities', { status: err.response?.status });
      setError('Failed to search cities. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [isReadOnly]);

  // Get exchange rate when date or currency changes.
  // `background` fetches (the automatic ones) skip the full-page overlay and
  // leave any message on screen alone: when a batch of invoice rows fills in,
  // one fetch per row used to blank the page repeatedly and wipe the error
  // the user was reading.
  const getExchangeRate = useCallback(async (id, item, { background = false } = {}) => {
    if (!item || !item.expenditureDate || !item.expenditureCurrency || isReadOnly) {
      return;
    }

    if (!background) {
      setIsLoading(true);
      setError(null);
    }

    try {
      // The LOCAL calendar day. DateInput holds local midnight, and
      // toISOString() turned that into the previous day anywhere east of UTC
      // (all SEED offices), so rates were looked up for the wrong date.
      const date = formatLocalDate(item.expenditureDate);
      
      const currencyCode = item.expenditureCurrency;
      
      const rateData = await CurrencyService.getExchangeRate(date, currencyCode);

      // Round the rate to 2dp (per requirement) and derive USD from that
      // rounded rate so the displayed figures reconcile. An empty or zero
      // answer is NOT 1 for a foreign currency: it stays 0, so submit names
      // the line instead of claiming the receipt amount as dollars.
      const fetched = Math.round((parseFloat(rateData?.rate) || 0) * 100) / 100;
      const rate = rateOrFallback(currencyCode, fetched);

      setExpenseItems(prev => {
        let changed = false;
        const next = prev.map(expItem => {
          if (expItem.id !== id) return expItem;
          // The row moved on while this was in flight (another currency or
          // date - several rows fill at once when invoices are read): this
          // rate is for something it no longer holds, and the row's newer
          // lookup prices it.
          if (expItem.expenditureCurrency !== currencyCode
              || formatLocalDate(expItem.expenditureDate) !== date) {
            return expItem;
          }
          const usdEquivalent = usdAt(expItem.receiptAmount, rate);
          if (parseFloat(expItem.exchangeRate) === rate
              && parseFloat(expItem.usdEquivalent) === usdEquivalent) {
            return expItem;
          }
          changed = true;
          return {
            ...expItem,
            exchangeRate: rate,
            usdEquivalent
          };
        });
        // Nothing new (e.g. a lookup that again came back empty): keep the
        // same array, so the auto-fetch effect is not re-run into a loop.
        return changed ? next : prev;
      });
    } catch (err) {
      secureLogger.error('Error getting exchange rate', { status: err.response?.status });
      setError(`Failed to get exchange rate for ${item.expenditureCurrency}. ${err.message}`);
    } finally {
      if (!background) setIsLoading(false);
    }
  }, [isReadOnly]);

  // One debounce PER ROW. A single shared debounce kept only the last call in
  // each 300ms window, so when several rows got a date and currency at once
  // (a batch of invoices being read) only the last row was priced; the rest
  // sat at a rate of 0 until the user edited them. Each row still collapses
  // its own rapid edits into one request.
  const debouncedGetExchangeRate = useMemo(() => {
    const perRow = new Map();
    return (id, item) => {
      let fetchRate = perRow.get(id);
      if (!fetchRate) {
        fetchRate = debounce((rowId, rowItem) => {
          getExchangeRate(rowId, rowItem, { background: true });
        }, 300);
        perRow.set(id, fetchRate);
      }
      fetchRate(id, item);
    };
  }, [getExchangeRate]);

  // Handle city selection
  const handleCitySelect = useCallback(async (id, city, country) => {
      if (isReadOnly) return;
      
      // Temporarily disable auto-fetch during city selection
      setAutoFetchExchangeRates(false);
      
      setIsLoading(true);
      
      try {
        const locationDetails = await LocationService.getLocationDetails(city, country);
        
        setExpenseItems(prev => {
          const updatedItems = prev.map(item => {
            if (item.id === id) {
              const expenditureCurrency = locationDetails.currency || item.expenditureCurrency;
              // A new currency drops the old one's rate, as in updateExpenseItem;
              // the lookup below (or the auto-fetch) prices it again.
              const rateReset = expenditureCurrency !== item.expenditureCurrency
                ? {
                  exchangeRate: rateOrFallback(expenditureCurrency, 0),
                  usdEquivalent: usdAt(item.receiptAmount, rateOrFallback(expenditureCurrency, 0))
                }
                : {};
              return {
                ...item,
                expenditureLocation: `${city}, ${country}`, // Changed from 'location' to 'expenditureLocation'
                expenditureCurrency,
                ...rateReset
              };
            }
            return item;
          });
          
          return updatedItems;
        });
        
        // Re-enable auto-fetch after a delay and manually trigger if needed
        setTimeout(() => {
          setAutoFetchExchangeRates(true);
          
          const updatedItem = expenseItems.find(item => item.id === id);
          if (updatedItem && updatedItem.expenditureDate && locationDetails.currency && updatedItem.receiptAmount) {
            const itemWithNewCurrency = {
              ...updatedItem,
              expenditureCurrency: locationDetails.currency
            };
            getExchangeRate(id, itemWithNewCurrency);
          }
        }, 1000);
        
      } catch (err) {
        secureLogger.error('Error getting location details', { status: err.response?.status });
        setError('Failed to get location details. Please try again.');
        setAutoFetchExchangeRates(true); // Re-enable even on error
      } finally {
        setIsLoading(false);
        setCityResults([]);
        setActiveSearchId(null);
      }
    }, [getExchangeRate, isReadOnly, expenseItems]);
  // Effect to get exchange rate when date or currency changes
  useEffect(() => {
    if (isReadOnly || !autoFetchExchangeRates) return;
    
    expenseItems.forEach(item => {
      if (
        item.expenditureDate && 
        item.expenditureCurrency && 
        item.receiptAmount && 
        parseFloat(item.receiptAmount) > 0 &&
        (!item.exchangeRate || item.exchangeRate === 0)
      ) {
        setPendingExchangeRateItems(prev => {
          if (!prev.has(item.id)) {
            const newSet = new Set(prev);
            newSet.add(item.id);
            
            setTimeout(() => {
              setPendingExchangeRateItems(current => {
                const updated = new Set(current);
                updated.delete(item.id);
                return updated;
              });
            }, 2000);
            
            debouncedGetExchangeRate(item.id, item);
            return newSet;
          }
          return prev;
        });
      }
    });
  }, [expenseItems, debouncedGetExchangeRate, isReadOnly, autoFetchExchangeRates]);

  const updateExpenseItem = useCallback((id, field, value) => {
    if (isReadOnly) return;

    setExpenseItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // The user chose (or cleared) the date, so it is no longer a placeholder.
          if (field === 'expenditureDate') delete updatedItem.expenditureDateIsDefault;

          // Special handling for ID updates (when backend returns real item ID)
          if (field === 'id') {
            updatedItem.id = value;
            return updatedItem; // Return early for ID updates
          }

          // A rate belongs to one currency on one day. Kept across a change,
          // the old one stood in until the new lookup landed - and for good if
          // that lookup failed, so a USD row switched to ETB stayed at 1:1.
          // Cleared instead (1 for USD); the lookup below or the auto-fetch
          // prices the row, and submit refuses it until then.
          const currencyChanged = field === 'expenditureCurrency'
            && String(value || '') !== String(item.expenditureCurrency || '');
          const dateChanged = field === 'expenditureDate'
            && formatLocalDate(value) !== formatLocalDate(item.expenditureDate);
          if (currencyChanged || dateChanged) {
            updatedItem.exchangeRate = rateOrFallback(updatedItem.expenditureCurrency, 0);
            updatedItem.usdEquivalent = usdAt(updatedItem.receiptAmount, updatedItem.exchangeRate);
          }

          if (field === 'receiptAmount') {
            // Not `|| 1`: with no rate yet, a foreign amount has no USD figure.
            const rate = rateOrFallback(item.expenditureCurrency, item.exchangeRate);
            updatedItem.usdEquivalent = usdAt(value, rate);
          }

          // Auto-fetch exchange rate logic (existing code)
          if (autoFetchExchangeRates && 
              (field === 'expenditureDate' || field === 'expenditureCurrency') &&
              updatedItem.expenditureDate && 
              updatedItem.expenditureCurrency &&
              updatedItem.receiptAmount && 
              parseFloat(updatedItem.receiptAmount) > 0) {
            
            setTimeout(() => {
              debouncedGetExchangeRate(id, updatedItem);
            }, 500);
          }

          return updatedItem;
        }
        return item;
      });

      return updatedItems;
    });
  }, [debouncedGetExchangeRate, isReadOnly, autoFetchExchangeRates]);
  // Save expense form
  // const saveExpenses = useCallback(async (isDraft = false, isAdminEdit = false) => {
  //   if (isReadOnly && !isAdminEdit) return;
    
  //   setIsSaving(true);
  //   setError(null);
    
  //   try {
  //     console.log('Saving expenses:', expenseItems);
      
  //     // Validate required fields
  //     if (expenseItems.length === 0) {
  //       setError('Please add at least one expense item');
  //       return;
  //     }

  //     const invalidItems = expenseItems.filter(item => 
  //       !item.expenditureDate || 
  //       !item.expenditureDescription || 
  //       !item.receiptAmount || 
  //       !item.expenditureCurrency
  //     );

  //     if (invalidItems.length > 0) {
  //       setError('Please fill in all required fields for each expense item');
  //       return;
  //     }
      
  //     let result;
      
  //     if (isAdminEdit) {
  //       // Admin edit mode - use admin-specific endpoint
  //       const claimData = {
  //         items: expenseItems,
  //         totalAmount: expenseItems.reduce((sum, item) => sum + (parseFloat(item.usdEquivalent) || 0), 0),
  //         isAdminEdit: true,
  //         adminComments: expenseItems.map(item => ({
  //           itemId: item.id,
  //           comment: item.adminComments || '',
  //           description: item.expenditureDescription
  //         })).filter(comment => comment.comment)
  //       };
        
  //       result = await ExpenseService.adminUpdateExpenseClaim(claimId, claimData, isDraft);
        
  //       console.log('Admin save result:', result);
  //       alert('Admin changes saved successfully!');
        
  //       // Navigate back to admin claim review page
  //       window.location.href = `/admin/claim/expense/${claimId}`;
        
  //     } else if (isViewMode && claimId) {
  //       // Regular user updating existing claim
  //       result = await ExpenseService.updateExpenseClaim(claimId, expenseItems, isDraft);
        
  //       console.log('Update result:', result);
  //       alert(isDraft ? 'Draft updated successfully!' : 'Claim updated successfully!');
  //       navigate('/dashboard');
        
  //     } else {
  //       // Creating new claim
  //       result = await ExpenseService.saveExpenseClaim(null, expenseItems, isDraft);
        
  //       console.log('Create result:', result);
  //       alert(isDraft ? 'Draft saved successfully!' : 'Claim submitted successfully!');
  //       navigate('/dashboard');
  //     }
      
  //     setSaveSuccess(true);
  //     setTimeout(() => {
  //       setSaveSuccess(false);
  //     }, 1500);
      
  //   } catch (err) {
  //     console.error('Error saving expenses:', err);
  //     const errorMessage = err.response?.data?.message || err.message || 'Please try again.';
  //     setError('Failed to save expenses: ' + errorMessage);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // }, [expenseItems, isReadOnly, isViewMode, claimId, navigate]);


const deleteClaim = async (claimId) => {

  setIsLoading(true);
  setError(null);

  try {

    const data = await invoiceService.deleteExpenseClaim(claimId);

    // Check if we got data back (assuming successful deletion returns data)
    if (data) {
      navigate('/dashboard');
      return data;
    } else {
      const errorMsg = 'Delete operation completed but no confirmation received';
      setError(errorMsg);
      return null;
    }
  } catch (err) {
    const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete claim';
    setError(errorMessage);
    return null;
  } finally {
    setIsLoading(false);
  }
}
  // Effect to clean up city results when search is cleared
  useEffect(() => {
    if (citySearch.length < 2) {
      setCityResults([]);
    }
  }, [citySearch]);


  const toggleAdminComment = useCallback((itemId) => {
  setExpenseItems(prevItems => 
    prevItems.map(item => 
      item.id === itemId 
        ? { ...item, showAdminComment: !item.showAdminComment }
        : { ...item, showAdminComment: false } // Close other comment sections
    )
  );
}, []);

// Function to save admin comment
const saveAdminComment = useCallback((itemId) => {
  // Just close the comment section - the comment is already saved in the item
  setExpenseItems(prevItems => 
    prevItems.map(item => 
      item.id === itemId 
        ? { ...item, showAdminComment: false }
        : item
    )
  );
}, []);

  // Calculate total USD amount
  const totalUsdAmount = expenseItems.reduce((sum, item) => sum + (parseFloat(item.usdAmount) || 0), 0);

// In useExpenseForm.mjs - update the return statement at the end
return {
  expenseItems,
  citySearch,
  setCitySearch,
  cityResults,
  setCityResults,
  activeSearchId,
  setActiveSearchId,
  isSearching,
  isLoading,
  setIsLoading, // ADD THIS
  error,
  setError, // ADD THIS (if not already there)
  isSaving,
  saveSuccess,
  purposeAlertIds,
  addExpenseItem,
  deleteExpenseItem,
  deleteClaim,
  updateExpenseItem,
  searchCities,
  handleCitySelect,
  getExchangeRate,
  saveExpenses,
  debouncedSearch,
  setDebouncedSearch,
  totalUsdAmount,
  
  // View/Edit mode properties
  isViewMode,
  isEditing,
  isReadOnly,
  enableEditing,

  toggleAdminComment, 
  saveAdminComment,

  // Status-related returns
  claimStatus,
  setClaimStatus, // ADD THIS
  claimData,
  setClaimData, // ADD THIS
  // The claimant's name when this is a draft an admin is filing for them.
  onBehalfName: claimData?.filedOnBehalfBy && String(claimData?.userId) !== String(user?.id)
    ? `${claimData?.user?.firstName || ''} ${claimData?.user?.lastName || ''}`.trim()
    : null,
  adminComments,
  processedBy,
  processedAt,
  canEditClaim,
  loadClaimData,
  isAdminMode,
  isCreateRoute,
  
  // Admin modal returns
  showActionModal,
  setShowActionModal,
  selectedAction,
  setSelectedAction,
  adminDecisionComment,
  setAdminDecisionComment,
  handleAdminAction,
  withdrawApproval,
  handleModalSubmit,
  saveAdminChanges,

  lastName, 
  setLastName, 
  firstName, 
  setFirstName, 
  position, 
  setPosition,
  navigate,

  setExpenseItems
};
};

export default useExpenseForm;


